/**
 * Chat Database
 * 
 * IndexedDB storage for flattened LLM conversation history.
 * Uses Dexie for type-safe operations and reactivity.
 * 
 * Schema:
 * - content table: Stores FlatContent rows (one row per content item)
 * - Primary key: contentId (enables streaming updates via upsert)
 * - Compound indexes for efficient queries
 * 
 * Multi-stream support:
 * - All streams stored in same table
 * - Queries filtered by streamId
 * - Efficient compound indexes for per-stream operations
 */

import Dexie, { type Table } from 'dexie';
import type { FlatContent, EntryMetadata, MessageHeaderData } from '@/types/streamTypes';
import type { LLMHistory } from '@bloomstack/apis/llmTypes';

// ============================================================================
// Paged Sync Types (v2)
// ============================================================================

/**
 * Per-page content hash entry — mirrors StreamPageHash on the backend.
 * Stored in `stream_page_hashes` Dexie table.
 */
export interface StreamPageHashEntry {
  streamId: string;
  pageIndex: number;
  hash: string;
  entryCount: number;
  /** Viewport width at measurement time (px). Used for resize estimation. */
  measuredWidth?: number;
  /** Measured DOM height of this page (px). */
  measuredHeight?: number;
  /** Epoch ms when this hash was last confirmed valid from the backend. */
  lastValidatedAt: number;
}

/**
 * Raw page entries record — stores the LLMHistory[] for one page.
 * Stored in `stream_page_entries` Dexie table.
 */
export interface StreamPageEntryRecord {
  streamId: string;
  pageIndex: number;
  entries: LLMHistory[];
  /** Flat array of all entry IDs in this page — used for multi-entry index. */
  entryIds: string[];
}

/**
 * Minimap cache entry - stores pre-rendered canvas images as WebP blobs
 */
export interface MinimapCacheEntry {
  cacheKey: string;      // hash(streamId, theme, ...contentIds)
  streamId: string;      // For bulk deletion by stream
  imageBlob: Blob;       // WebP compressed canvas
  width: number;
  height: number;
  createdAt: number;     // For LRU eviction
}

/**
 * Height cache entry - stores measured DOM heights for Virtuoso stability
 * Separate table to avoid triggering content live queries on height updates
 */
export interface HeightCacheEntry {
  contentId: string;     // Primary key, matches content table
  height: number;        // Measured DOM height in pixels
}

export class ChatDatabase extends Dexie {
  // Typed table references
  content!: Table<FlatContent, string>;
  minimap_cache!: Table<MinimapCacheEntry, string>;
  height_cache!: Table<HeightCacheEntry, string>;
  // v2 paged sync tables
  stream_page_hashes!: Table<StreamPageHashEntry, [string, number]>;
  stream_page_entries!: Table<StreamPageEntryRecord, [string, number]>;

  constructor() {
    super('AIMysticChat');
    
    // Version 1 schema
    this.version(1).stores({
      content: `
        contentId,
        [streamId+sequenceNum+contentIndex],
        [streamId+timestamp],
        streamId,
        entryId,
        userId,
        role,
        type,
        internal,
        timestamp,
        sequenceNum,
        contentIndex
      `.replace(/\s+/g, ' ').trim(),
      minimap_cache: 'cacheKey, streamId, createdAt'
    });

    // Version 2: Add parentEntryId index for cascade deletes
    this.version(2).stores({
      content: `
        contentId,
        [streamId+sequenceNum+contentIndex],
        [streamId+timestamp],
        streamId,
        entryId,
        parentEntryId,
        userId,
        role,
        type,
        internal,
        timestamp,
        sequenceNum,
        contentIndex
      `.replace(/\s+/g, ' ').trim(),
      minimap_cache: 'cacheKey, streamId, createdAt'
    });

    // Version 3: Add height_cache table for Virtuoso stability
    // Separate table to avoid triggering content live queries on height updates
    this.version(3).stores({
      content: `
        contentId,
        [streamId+sequenceNum+contentIndex],
        [streamId+timestamp],
        streamId,
        entryId,
        parentEntryId,
        userId,
        role,
        type,
        internal,
        timestamp,
        sequenceNum,
        contentIndex
      `.replace(/\s+/g, ' ').trim(),
      minimap_cache: 'cacheKey, streamId, createdAt',
      height_cache: 'contentId'
    });

    // Version 4: Add rootEntryId for efficient edit operations across nested entries
    this.version(4).stores({
      content: `
        contentId,
        [streamId+sequenceNum+contentIndex],
        [streamId+timestamp],
        streamId,
        entryId,
        parentEntryId,
        rootEntryId,
        userId,
        role,
        type,
        internal,
        timestamp,
        sequenceNum,
        contentIndex
      `.replace(/\s+/g, ' ').trim(),
      minimap_cache: 'cacheKey, streamId, createdAt',
      height_cache: 'contentId'
    }).upgrade(async (trans) => {
      // Migrate existing data to populate rootEntryId
      const allContent = await trans.table('content').toArray();

      // Build parent map for efficient lookups (headers only)
      const entryMap = new Map<string, FlatContent>();
      allContent.forEach(item => {
        if (item.contentIndex === -1) { // Only store headers in map
          entryMap.set(item.entryId, item);
        }
      });

      // Cache for memoizing root lookups (prevents duplicate walks)
      const rootCache = new Map<string, string>();

      // Helper to find root entry ID by walking up parent chain
      const findRoot = (entryId: string, visited = new Set<string>()): string => {
        // Check cache first
        if (rootCache.has(entryId)) return rootCache.get(entryId)!;

        // Prevent infinite loops
        if (visited.has(entryId)) return entryId;
        visited.add(entryId);

        const entry = entryMap.get(entryId);
        if (!entry || !entry.parentEntryId) {
          rootCache.set(entryId, entryId);
          return entryId;
        }

        const root = findRoot(entry.parentEntryId, visited);
        rootCache.set(entryId, root); // Cache the result
        return root;
      };

      // Update all content with rootEntryId
      const updates = allContent.map(item => ({
        ...item,
        rootEntryId: findRoot(item.entryId)
      }));

      await trans.table('content').bulkPut(updates);
    });

    // Version 5: Add lastSyncedAt index for TTL eviction
    this.version(5).stores({
      content: `
        contentId,
        [streamId+sequenceNum+contentIndex],
        [streamId+timestamp],
        streamId,
        entryId,
        parentEntryId,
        rootEntryId,
        userId,
        role,
        type,
        internal,
        timestamp,
        sequenceNum,
        contentIndex,
        lastSyncedAt
      `.replace(/\s+/g, ' ').trim(),
      minimap_cache: 'cacheKey, streamId, createdAt',
      height_cache: 'contentId'
    });

    // Version 6: Add paged sync tables (v2 scroller)
    this.version(6).stores({
      content: `
        contentId,
        [streamId+sequenceNum+contentIndex],
        [streamId+timestamp],
        streamId,
        entryId,
        parentEntryId,
        rootEntryId,
        userId,
        role,
        type,
        internal,
        timestamp,
        sequenceNum,
        contentIndex,
        lastSyncedAt
      `.replace(/\s+/g, ' ').trim(),
      minimap_cache: 'cacheKey, streamId, createdAt',
      height_cache: 'contentId',
      // [streamId+pageIndex] compound primary key; streamId for bulk queries
      stream_page_hashes: '[streamId+pageIndex], streamId',
      // [streamId+pageIndex] compound primary key; *entryIds multi-entry index for lookup by entry ID
      stream_page_entries: '[streamId+pageIndex], streamId, *entryIds',
    });
  }
}

/**
 * Evict stale content from Dexie based on lastSyncedAt TTL.
 * Should be called once at app startup (fire-and-forget).
 */
export async function evictStaleContent(ttlMs: number = DEXIE_TTL_MS): Promise<number> {
  const cutoff = Date.now() - ttlMs;
  // Delete all content where lastSyncedAt is defined and older than cutoff
  const deleted = await chatDb.content
    .where('lastSyncedAt')
    .below(cutoff)
    .delete();
  return deleted;
}

/** TTL for Dexie cache entries — 2 weeks */
const DEXIE_TTL_MS = 14 * 24 * 60 * 60 * 1000;

// Export singleton instance
export const chatDb = new ChatDatabase();

// ============================================================================
// PAGED SYNC HELPER METHODS (v2)
// ============================================================================

/** Get all stored page hashes for a stream. */
export async function getStreamPageHashes(streamId: string): Promise<StreamPageHashEntry[]> {
  return chatDb.stream_page_hashes.where('streamId').equals(streamId).toArray();
}

/** Upsert a page hash entry (insert or replace). */
export async function upsertStreamPageHash(entry: StreamPageHashEntry): Promise<void> {
  await chatDb.stream_page_hashes.put(entry);
}

/** Delete page hash entries for a list of page indices. */
export async function deleteStreamPageHashes(streamId: string, pageIndices: number[]): Promise<void> {
  const keys = pageIndices.map((pi) => [streamId, pi] as [string, number]);
  await chatDb.stream_page_hashes.bulkDelete(keys);
}

/** Get stored LLMHistory entries for a page. Returns null if not found. */
export async function getStreamPageEntries(
  streamId: string,
  pageIndex: number,
): Promise<LLMHistory[] | null> {
  const record = await chatDb.stream_page_entries.get([streamId, pageIndex]);
  return record?.entries ?? null;
}

/** Upsert raw LLMHistory entries for a page. */
export async function upsertStreamPageEntries(
  streamId: string,
  pageIndex: number,
  entries: LLMHistory[],
): Promise<void> {
  const entryIds = entries.map((e) => e.id).filter((id): id is string => id != null);
  await chatDb.stream_page_entries.put({ streamId, pageIndex, entries, entryIds });
}

/** Delete page entry records for a list of page indices. */
export async function deleteStreamPageEntries(streamId: string, pageIndices: number[]): Promise<void> {
  const keys = pageIndices.map((pi) => [streamId, pi] as [string, number]);
  await chatDb.stream_page_entries.bulkDelete(keys);
}

/**
 * Find the page containing a given entry ID.
 * Returns the pageIndex, or null if not found.
 */
export async function findStreamPageByEntryId(
  streamId: string,
  entryId: string,
): Promise<number | null> {
  const record = await chatDb.stream_page_entries
    .where('[streamId+pageIndex]')
    .between([streamId, Dexie.minKey], [streamId, Dexie.maxKey])
    .filter((r) => r.entryIds.includes(entryId))
    .first();
  return record?.pageIndex ?? null;
}
// QUERY HELPERS
// ============================================================================

/**
 * Delete all content rows for a given entry and its children.
 * Used when ENTRY_REMOVED event fires.
 * 
 * Cascade behavior: When a parent entry is deleted, the backend
 * automatically deletes child entries via Prisma cascade. However,
 * only one entry_removed event is emitted (for the parent).
 * This function handles the frontend cleanup by also deleting
 * content from any child entries.
 * 
 * Also cleans up the height_cache table for deleted content.
 * 
 * @param entryId - The entry ID to delete content for
 * @returns Number of rows deleted (parent + children)
 */
export async function deleteByEntryId(entryId: string): Promise<number> {
  // Get all contentIds we're about to delete (for height cache cleanup)
  const contentToDelete = await chatDb.content.where('entryId').equals(entryId).toArray();
  const childContentToDelete = await getChildContentByParentEntryId(entryId);
  const allContentIds = [
    ...contentToDelete.map(c => c.contentId),
    ...childContentToDelete.map(c => c.contentId),
  ];
  
  // Delete content for this entry
  const parentDeleted = await chatDb.content.where('entryId').equals(entryId).delete();
  
  // Also delete content for any child entries (cascade)
  const childrenDeleted = await deleteChildrenByParentEntryId(entryId);
  
  // Clean up height cache for deleted content
  if (allContentIds.length > 0) {
    await chatDb.height_cache.bulkDelete(allContentIds);
  }
  
  return parentDeleted + childrenDeleted;
}

/**
 * Recursively get all content rows for child entries of a given parent.
 * Used for height cache cleanup.
 */
async function getChildContentByParentEntryId(parentEntryId: string): Promise<FlatContent[]> {
  const childContent = await chatDb.content
    .where('parentEntryId')
    .equals(parentEntryId)
    .toArray();
  
  if (childContent.length === 0) {
    return [];
  }
  
  // Get unique child entry IDs
  const childEntryIds = [...new Set(childContent.map(c => c.entryId))];
  
  // Recursively get grandchildren
  const grandchildren: FlatContent[] = [];
  for (const childEntryId of childEntryIds) {
    grandchildren.push(...await getChildContentByParentEntryId(childEntryId));
  }
  
  return [...childContent, ...grandchildren];
}

/**
 * Recursively delete all content rows for child entries of a given parent.
 * 
 * @param parentEntryId - The parent entry ID to find children for
 * @returns Number of rows deleted
 */
async function deleteChildrenByParentEntryId(parentEntryId: string): Promise<number> {
  // Find all content rows that belong to direct child entries
  const childContent = await chatDb.content
    .where('parentEntryId')
    .equals(parentEntryId)
    .toArray();
  
  if (childContent.length === 0) {
    return 0;
  }
  
  // Get unique child entry IDs
  const childEntryIds = [...new Set(childContent.map(c => c.entryId))];
  
  // Delete content for direct children
  const deleted = await chatDb.content
    .where('parentEntryId')
    .equals(parentEntryId)
    .delete();
  
  // Recursively delete grandchildren
  let grandchildrenDeleted = 0;
  for (const childEntryId of childEntryIds) {
    grandchildrenDeleted += await deleteChildrenByParentEntryId(childEntryId);
  }
  
  return deleted + grandchildrenDeleted;
}

/**
 * Get entry-level metadata for content events.
 * Used when CONTENT_ADDED/UPDATED events fire and we need entry context.
 * 
 * Lookup order:
 * 1. If contentId provided, look up that exact row
 * 2. Fall back to any row with the given entryId
 * 
 * @param contentId - The content ID to look up first (optional)
 * @param entryId - The entry ID to fall back to
 * @returns EntryMetadata or null if no row found
 */
export async function getEntryMetadata(contentId: string | undefined, entryId: string): Promise<EntryMetadata | null> {
  let row: FlatContent | undefined;

  // First try: look up by contentId
  if (contentId) {
    row = await chatDb.content.get(contentId);
  }

  // Fall back: look up any row by entryId
  if (!row) {
    row = await chatDb.content
      .where('entryId')
      .equals(entryId)
      .first();
  }

  if (!row) {
    return null;
  }

  // Extract metadata from the row
  // userProfile only available on message_header rows
  const userProfile = row.type === 'message_header' 
    ? (row.data as MessageHeaderData).userProfile 
    : undefined;

  return {
    userId: row.userProfileId,
    role: row.role,
    timestamp: row.timestamp,
    sequenceNum: row.sequenceNum,
    internal: row.internal,
    parentEntryId: row.parentEntryId ?? undefined,
    userProfile,
  };
}
